import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { Icon, H5, Tooltip, Text } from '../../../../../components';
import { AnswerSummary, QuestionHistoryItem } from '../../atoms';
import { QuestionHistoryToggle } from '../QuestionHistoryToggle';
import { StepsTracker } from '../StepsTracker';
import { ChildQuestionsToggle } from '../ChildQuestionsToggle';

const ChatAssetContainer = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
`;

const ChatAssetInnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border-radius: 8px;
  border: ${({ $selected }) =>
    $selected ? '1px solid var(--border-default)' : '1px solid var(--border-muted)'};
  box-shadow: ${({ $selected }) => ($selected ? '0px 2px 5px 0px rgba(0, 0, 0, 0.15)' : 'none')};
  background-color: var(--elevation-high);
  margin: 12px 0;
  cursor: pointer;

  &:hover {
    border: 1px solid var(--border-default);
  }
`;

const AssetContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const StyledIcon = styled(Icon)`
  align-self: flex-start;
  padding: 8px;
  border-radius: 8px;
  background-color: var(--gray-4);
`;

const AssetNameAndDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ChatAssetFooterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const QuestionActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`;

const QuestionAction = styled(Icon)`
  cursor: pointer;
  border-radius: 4px;
  &:hover {
    background: var(--wash-gray-hover);
  }
  padding: 2px;
`;

const AssetTitleLabel = styled(H5)`
  word-break: break-word;
`;

const StyledText = styled(Text)`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 2px 4px;
  gap: 4px;
  background-color: ${({ $backgroundGray }) =>
    $backgroundGray ? 'var(--gray-8)' : 'var(--wash-orange-default)'};
  width: fit-content;
  margin-top: 6px;
`;

const QuestionHistoryItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ChatAsset = ({
  title,
  isSelected,
  onQuestionSelect,
  onSwitchToChartMode,
  setActiveChildQuestion,
  activeChildQuestion,
  steps = null,
  onRetryQuestion = null,
  onUndoClick = null,
  onCreateNewBranch = null,
  isCurrentBranchQuestion = false,
  isLastQuestion = false,
  isReadOnly = false,
  isSuccessfulQuestion = false,
  isSaved = false,
  isSqlChanged = false,
  dataDescription = null,
  isSavedAssetQuestion = false,
  currentOrganizationName = null,
  hasChart = false,
  contextQuestions = [],
  isChartFollowUp = false
}) => {
  const renderQuestionActions = () => (
    <QuestionActionsContainer>
      {!isCurrentBranchQuestion && !isLastQuestion && !isChartFollowUp && (
        <>
          <Tooltip content='Create a chart'>
            <QuestionAction
              name='bar-chart'
              color='var(--content-subtitle)'
              displaySize={20}
              onClick={onSwitchToChartMode}
            />
          </Tooltip>
          <Tooltip content='Follow up in a different direction'>
            <QuestionAction
              name='follow-up'
              color='var(--content-subtitle)'
              displaySize={20}
              onClick={onCreateNewBranch}
            />
          </Tooltip>
        </>
      )}
      {isLastQuestion && !isReadOnly && (
        <>
          {!isChartFollowUp && (
            <Tooltip content='Create a chart'>
              <QuestionAction
                name='bar-chart'
                color='var(--content-subtitle)'
                displaySize={20}
                onClick={onSwitchToChartMode}
              />
            </Tooltip>
          )}
          {!isChartFollowUp && (
            <Tooltip content='Have Supper try to answer this again'>
              <QuestionAction
                name='refresh'
                color='var(--content-subtitle)'
                displaySize={20}
                onClick={onRetryQuestion}
              />
            </Tooltip>
          )}
          <Tooltip content='Remove this question'>
            <QuestionAction
              name='trash'
              color='var(--content-subtitle)'
              displaySize={20}
              onClick={onUndoClick}
            />
          </Tooltip>
        </>
      )}
    </QuestionActionsContainer>
  );

  return (
    <ChatAssetContainer>
      <ChatAssetInnerContainer $selected={isSelected} onClick={() => onQuestionSelect()}>
        <AssetContentContainer>
          <StyledIcon name='table' color='var(--content-subtitle)' />
          <AssetNameAndDescriptionContainer>
            <AssetTitleLabel>{title}</AssetTitleLabel>
            {dataDescription && <AnswerSummary text={dataDescription} />}
            <BadgeContainer>
              {isSaved && !isSavedAssetQuestion && (
                <StyledText $small $color='var(--primary-default)'>
                  <Icon name='bookmark' displaySize={16} color='var(--primary-default)' />
                  Saved
                </StyledText>
              )}
              {isSqlChanged && !isChartFollowUp && (
                <StyledText $small $color='var(--lavender-100)' $backgroundGray>
                  <Icon name='cylinder' displaySize={16} color='var(--lavender-100)' />
                  Modified SQL
                </StyledText>
              )}
              {(hasChart || isChartFollowUp) && (
                <StyledText $small $color='var(--lavender-100)' $backgroundGray>
                  <Icon name='bar-chart' displaySize={16} color='var(--lavender-100)' />
                  Chart
                </StyledText>
              )}
            </BadgeContainer>
            {isSavedAssetQuestion && (
              <StyledText $small $color='var(--lavender-100)' $backgroundGray>
                <Icon name='box' displaySize={16} color='var(--lavender-100)' />
                {currentOrganizationName} asset
              </StyledText>
            )}
            {contextQuestions?.length > 0 && (
              <QuestionHistoryToggle>
                <QuestionHistoryItemsContainer>
                  {contextQuestions?.map((contextQuestion) => (
                    <QuestionHistoryItem
                      key={contextQuestion.id}
                      question={contextQuestion.question}
                    />
                  ))}
                </QuestionHistoryItemsContainer>
              </QuestionHistoryToggle>
            )}
          </AssetNameAndDescriptionContainer>
        </AssetContentContainer>
      </ChatAssetInnerContainer>
      <ChatAssetFooterContainer>
        {/* question with child question, to prevent rendering question actions twice */}
        {steps.length > 0 && (
          <ChildQuestionsToggle
            questionActions={
              isSuccessfulQuestion &&
              !isCurrentBranchQuestion &&
              !isSavedAssetQuestion &&
              renderQuestionActions()
            }
          >
            {/* steps.length + 1 to mark q as complete */}
            <StepsTracker
              steps={steps}
              currentStep={steps.length + 1}
              setActiveChildQuestion={setActiveChildQuestion}
              activeChildQuestion={activeChildQuestion}
            />
          </ChildQuestionsToggle>
        )}
        {/* question with no child questions to prevent rendering question actions twice */}
        {steps.length === 0 &&
          isSuccessfulQuestion &&
          !isCurrentBranchQuestion &&
          !isSavedAssetQuestion &&
          renderQuestionActions()}
      </ChatAssetFooterContainer>
    </ChatAssetContainer>
  );
};

ChatAsset.propTypes = {
  title: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onQuestionSelect: PropTypes.func.isRequired,
  steps: PropTypes.arrayOf(PropTypes.string),
  isSaved: PropTypes.bool.isRequired,
  isSqlChanged: PropTypes.bool,
  dataDescription: PropTypes.string,
  isSavedAssetQuestion: PropTypes.bool,
  currentOrganizationName: PropTypes.string,
  onRetryQuestion: PropTypes.func,
  onUndoClick: PropTypes.func,
  onCreateNewBranch: PropTypes.func,
  isCurrentBranchQuestion: PropTypes.bool,
  isLastQuestion: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isSuccessfulQuestion: PropTypes.bool,
  contextQuestions: PropTypes.arrayOf(PropTypes.string),
  hasChart: PropTypes.bool,
  onSwitchToChartMode: PropTypes.func.isRequired,
  isChartFollowUp: PropTypes.bool,
  setActiveChildQuestion: PropTypes.func.isRequired,
  activeChildQuestion: PropTypes.number.isRequired
};

export { ChatAsset };
